import React from 'react'

import { EmptyCell } from '@/components/library-v2/catalog/tables/full/Table/cells'
import {
  BooleanRenderer,
  CurrencyRenderer,
  DateRenderer,
  DropdownRenderer,
  MultiSelectRenderer,
  NumberListRenderer,
  NumberRenderer,
  StringListRenderer,
  StringRenderer,
} from '@/components/library-v2/catalog/ui/Field/Render'

import { getStoredExtendedValues } from '@/helpers/extended-schema'

import type { ExtendedSchemaDerivedField } from '@/types/extended-schema'
import type { Pursuit } from '@/types/pursuit/Pursuit'

// Helper function to get field value from Pursuit (uses extended_data instead of values)
export function getFieldValueFromPursuit<F extends ExtendedSchemaDerivedField>(
  pursuit: Pursuit,
  field: F,
): any {
  return getStoredExtendedValues(pursuit.extended_data)[field.id]
}

// Render field for pursuit (similar to renderField but for pursuits)
export const renderPursuitField = (
  pursuit: Pursuit,
  field: ExtendedSchemaDerivedField,
  className?: string,
): JSX.Element | null => {
  const type = field.type
  switch (type) {
    case 'string': {
      const value = getFieldValueFromPursuit(pursuit, field)
      return <StringRenderer value={value} className={className} />
    }
    case 'number': {
      const value = getFieldValueFromPursuit(pursuit, field)
      return <NumberRenderer value={value} className={className} />
    }
    case 'boolean': {
      const value = getFieldValueFromPursuit(pursuit, field)
      return <BooleanRenderer value={value} className={className} />
    }
    case 'string_list': {
      const value = getFieldValueFromPursuit(pursuit, field)
      return (
        <StringListRenderer
          value={value}
          columnId={field.id}
          className={className}
        />
      )
    }
    case 'number_list': {
      const value = getFieldValueFromPursuit(pursuit, field)
      return (
        <NumberListRenderer
          value={value}
          columnId={field.id}
          className={className}
        />
      )
    }
    case 'date': {
      const value = getFieldValueFromPursuit(pursuit, field)
      return <DateRenderer value={value} className={className} />
    }
    case 'dropdown': {
      const value = getFieldValueFromPursuit(pursuit, field)
      return <DropdownRenderer value={value} className={className} />
    }
    case 'multi_select': {
      const value = getFieldValueFromPursuit(pursuit, field)
      return (
        <MultiSelectRenderer
          value={value}
          columnId={field.id}
          className={className}
        />
      )
    }
    case 'currency': {
      const value = getFieldValueFromPursuit(pursuit, field)
      return <CurrencyRenderer value={value} className={className} />
    }
    default: {
      const exhaustiveCheck: never = type
      return <EmptyCell />
    }
  }
}

export const EMPTY_ESTIMATED_VALUE = '$ \u2013'
